import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {
    FORM_DIRECTIVES,
    FORM_PROVIDERS,
    FormControl,
    FormGroup,
    REACTIVE_FORM_DIRECTIVES,
    Validators
} from "@angular/forms";
import {AlertComponent, TYPEAHEAD_DIRECTIVES} from "ng2-bootstrap/ng2-bootstrap";
import {Vehicle} from "../domain/vehicle";
import {DATE_VALID_CHARACTERS, DATE_VALIDATOR, DEFAULT_ERROR_MESSAGES, FILE_MAX_SIZE} from "../validators/validators";
import {LoginService} from "../service/login.service";
import {Procedure} from "../domain/procedure";
import {ProcedureService} from "../service/procedure.service";
import {ProcedureType} from "../domain/procedureType";
import {ExpirationType} from "../domain/expirationType";
import {Observable} from "rxjs/Observable";
import {FILE_UPLOAD_DIRECTIVES, FileUploader} from "ng2-file-upload";
import {ProcedureDocument} from "../domain/ProcedureDocument";
import {Infraction} from "../domain/infraction";
import {NavigationService} from "../service/navigation.service";

@Component({
    selector: 'procedures-editor',
    template: require('./procedures.editor.component.html'),
    styles: [require('./procedures.editor.component.css')],
    directives: [
        FORM_DIRECTIVES,
        REACTIVE_FORM_DIRECTIVES,
        AlertComponent,
        TYPEAHEAD_DIRECTIVES,
        FILE_UPLOAD_DIRECTIVES
    ],
    providers: [FORM_PROVIDERS],
})
export class ProcedureEditorComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() vehicle: Vehicle;
    @Output() close = new EventEmitter();

    private sub: any;

    public procedureType: ProcedureType[];
    public expirationType: ExpirationType[];

    public errorMessages: any;
    public form: FormGroup;

    private vehicleId: string;

    public errorMessage: string;
    public successMessage: string;
    public warningMessage: string;

    public newProcedure: Procedure;

    public loading: boolean;

    public canEdit: boolean;

    private todayStr: string;


    public expirationTypeSelectedId: string;
    public procedureTypeSelectedId: string;

    public uploaderDocument: FileUploader;
    public hasDocumentDropZoneOver: boolean;

    @ViewChild('inputDocument') inputAct: ElementRef;

    public isUploading: boolean;

    constructor(private router: Router,
                private procedureService: ProcedureService,
                private route: ActivatedRoute,
                private loginService: LoginService,
                private navigationService: NavigationService) {
        this.init();
    }

    private init(): void {
        this.errorMessages = DEFAULT_ERROR_MESSAGES;

        this.newProcedure = new Procedure();

        this.loading = false;

        this.canEdit = false;

        let dateStrParts = new Date();
        this.todayStr = this.padLeft(dateStrParts.getDate().toString(), '0', 2) + '/' +
            this.padLeft((dateStrParts.getMonth() + 1).toString(), '0', 2) + '/' + dateStrParts.getFullYear();

        this.newProcedure.creationDate = this.todayStr;
        this.newProcedure.customDate = this.todayStr;

        this.vehicle = new Vehicle();

        this.uploaderDocument = new FileUploader({});
        this.hasDocumentDropZoneOver = false;

        this.isUploading = false;
    }

    private padLeft(text: string, padChar: string, size: number): string {
        return (String(padChar).repeat(size) + text).substr((size * -1), size);
    }

    private createFormGroup(): void {
        this.form = new FormGroup({
            type: new FormControl(this.vehicle.type.description),
            patent: new FormControl(this.vehicle.patent),
            brand: new FormControl(this.vehicle.brand.name),
            model: new FormControl(this.vehicle.model),
            year: new FormControl(this.vehicle.year),
            engineNumber: new FormControl(this.vehicle.engineNumber),
            newProcedureDate: new FormControl(this.newProcedure.creationDate, [Validators.required, DATE_VALID_CHARACTERS, DATE_VALIDATOR]),
            newProcedureExpirationType: new FormControl(this.newProcedure.expirationType, [Validators.required]),
            newProcedureType: new FormControl(this.newProcedure.type, [Validators.required]),
            newProcedureDescription: new FormControl(this.newProcedure.description),
            newProcedureComment: new FormControl(this.newProcedure.comment),
            newProcedureCustomDate: new FormControl(this.newProcedure.customDate, [Validators.required, DATE_VALID_CHARACTERS, DATE_VALIDATOR]),
        });
    }

    public ngOnInit() {
        let userLogged = this.loginService.getUserLogged();
        this.canEdit = userLogged.isActionAllowed('PROCEDURES', 'EDIT', 'PROCEDURES');

        this.createFormGroup();

        this.sub = this.route.params.subscribe(params => {
            this.vehicleId = params['vehicleId'];
            this.getData();
        });
    }

    public ngAfterViewInit() {
    }

    private getData(): void {
        this.loading = true;
        this.errorMessage = null;

        let forkTasks: Observable<any>[] = [];
        forkTasks.push(this.procedureService.getProcedureTypes());
        forkTasks.push(this.procedureService.getExpirationTypes());
        forkTasks.push(this.procedureService.getVehicle(this.vehicleId));

        Observable.forkJoin(forkTasks).subscribe(
            this.setData.bind(this),
            () => {
                this.bindError('Ocurrió un error al obtener los datos del vehículo.')
            });
    }

    public setData(data: any[]): void {
        this.loading = false;
        this.procedureType = data[0];
        this.expirationType = data[1];
        this.vehicle = data[2];

        this.expirationType = this.expirationType.filter(
            function (obj: ExpirationType) {
                return obj.description != 'NO VENCE';
            });

        this.procedureType = this.procedureType.filter(
            function (obj: ProcedureType) {
                return obj.name != 'LICENSE_PLATE';
            });

        this.expirationTypeSelectedId = this.expirationType[0].id;
        this.procedureTypeSelectedId = this.procedureType[0].id;

        this.expirationTypeChange(this.expirationTypeSelectedId);
        this.procedureTypeChange(this.procedureTypeSelectedId);

        this.updateUploaderUrl();

        this.procedureService.generateProcedureTempId()
            .subscribe(
                (response) => {
                    this.newProcedure.tempId = response.value;
                    this.updateUploaderUrl();
                },
                () => {
                    this.bindError('Ocurrió un error al generar un trámite.');
                });
    }

    public ngOnDestroy() {
        this.sub.unsubscribe();
    }

    private clearMessages(): void {
        this.successMessage = null;
        this.errorMessage = null;
        this.warningMessage = null;
    }

    public save(): void {
        this.clearMessages();
        this.isUploadingAFile();
        if (!this.checkAllBeforeSave()) {
            this.loading = true;

            this.procedureService.save(this.vehicle).subscribe(
                (saved: Vehicle) => {
                    this.loading = false;
                    this.vehicle = saved;
                    this.successMessage = 'El trámite se guardó con éxito';
                    this.updateViewAfterSave();

                    if (this.checkPreSaveProcedures()) {
                        this.bindWarning('Existen Trámites sin guardar.');
                    }
                },
                () => {
                    this.bindError('Ocurrió un error al guardar la información.');
                });
        } else {
            this.bindError('Existe un archivo pendiente de subida. Recuerde que el tamaño máximo del archivo a subir es de 10Mb.');
        }
    }

    public goBack(event: any): void {
        event.preventDefault();
        this.close.emit(this.vehicle);
        window.history.back();
    }

    private addNewProcedureToVehicle(): void {
        this.uploaderDocument.queue.forEach((item) => {
            this.newProcedure.document = new ProcedureDocument();
            this.newProcedure.document.id = "temp/" + this.newProcedure.tempId;
            this.newProcedure.document.name = item.file.name;
        });

        this.vehicle.procedures.push((JSON.parse(JSON.stringify(this.newProcedure))));
        this.newProcedure = new Procedure();
        this.newProcedure.creationDate = this.todayStr;
        this.newProcedure.customDate = this.todayStr;

        this.expirationTypeSelectedId = this.expirationType[0].id;
        this.procedureTypeSelectedId = this.procedureType[0].id;
        this.expirationTypeChange(this.expirationTypeSelectedId);
        this.procedureTypeChange(this.procedureTypeSelectedId);

        this.procedureService.generateProcedureTempId()
            .subscribe(
                (response) => {
                    this.newProcedure.tempId = response.value;
                    this.updateUploaderUrl();
                },
                () => {
                    this.bindError('Ocurrió un error al generar un trámite.');
                });

        this.uploaderDocument.clearQueue();
    }

    public addNewProcedure(event: any): void {
        event.preventDefault();
        this.addNewProcedureToVehicle();

        this.clearMessages();
        this.bindWarning('Existen Trámites sin guardar.');
    }

    public deleteProcedure(event: any, index: number, id: string, idTemp: string): void {
        event.preventDefault();

        this.clearMessages();
        this.loading = true;

        let notPending = false;
        if (id != undefined && id != null) {
            notPending = true;
            this.procedureService.delete(id).subscribe(
                () => {
                    this.loading = false;
                    this.successMessage = 'El trámite se eliminó con éxito';

                    this.loading = false;
                    this.vehicle.procedures.splice(index, 1);

                    if (this.checkPreSaveProcedures()) {
                        this.clearMessages();
                        this.bindWarning('Existen Trámites sin guardar.');
                    }
                },
                () => {
                    this.bindError('Ocurrió un error al eliminar el trámite.');
                });
        }
        if (idTemp != undefined && idTemp != null) {
            notPending = true;
            this.procedureService.deleteTempProcedureDocument(idTemp).subscribe(
                () => {
                    this.loading = false;
                    this.successMessage = 'La carga de trámite se eliminó con éxito';

                    this.loading = false;
                    this.vehicle.procedures.splice(index, 1);

                    if (this.checkPreSaveProcedures()) {
                        this.clearMessages();
                        this.bindWarning('Existen Trámites sin guardar.');
                    }
                },
                () => {
                    this.bindError('Ocurrió un error al eliminar el trámite.');
                });
        }

        if (!notPending) {
            this.loading = false;
            this.vehicle.procedures.splice(index, 1);

            if (this.checkPreSaveProcedures()) {
                this.bindWarning('Existen Trámites sin guardar.');
            }
        }
    }

    public editProcedure(event: any, item: Procedure): void {
        event.preventDefault();
        this.navigationService.setBackParameters({tabSelected: 'procedures'});
        this.router.navigate(['app/home/editSingleProcedure', item.id]); // Ya existia editProcedure, que lleva a esta vista en realidad
    }

    public expirationTypeChange(newValue: any): void {
        let result = this.expirationType.filter(function (obj: ExpirationType) {
            return obj.id == newValue;
        });
        if (result.length > 0) {
            this.newProcedure.expirationType = result[0];
        }
    }

    public procedureTypeChange(newValue: any): void {
        let result = this.procedureType.filter(function (obj: ExpirationType) {
            return obj.id == newValue;
        });
        if (result.length > 0) {
            this.newProcedure.type = result[0];
        }

    }

    private bindError(messageError: string): void {
        this.loading = false;
        if (this.errorMessage == null) {
            this.errorMessage = messageError;
        } else {
            this.errorMessage = this.errorMessage.toString() + ' ' + messageError;
        }
    }

    private bindWarning(messageWarning: string): void {
        this.loading = false;
        if (this.warningMessage == null) {
            this.warningMessage = messageWarning;
        } else {
            this.warningMessage = this.warningMessage.toString() + ' ' + messageWarning;
        }
    }

    public removeDocument(): void {
        this.newProcedure.document = null;
    }

    private updateUploaderUrl(): void {
        let fileOwnerId = this.newProcedure.tempId;
        this.uploaderDocument = new FileUploader({
            url: 'procedure/' + fileOwnerId + '/upload',
            autoUpload: true,
            maxFileSize: FILE_MAX_SIZE,
        });
    }

    public isUploadingAFile() {
        this.isUploading = this.inputAct && this.inputAct.nativeElement && this.inputAct.nativeElement.value != '' && this.uploaderDocument.queue && this.uploaderDocument.queue.length == 0;
    }

    public fileOverDocument(e: any): void {
        this.hasDocumentDropZoneOver = e;
    }

    public removeDocumentFromQuee(item: any): void {
        this.procedureService.deleteTempProcedureDocument(this.newProcedure.tempId).subscribe(
            (result) => item.remove(),
            () => {
                this.bindError('Ocurrió un error al eliminar el documento de trámite.');
            });
    }

    private checkAllBeforeSave() {
        return this.isUploading || !this.form.valid || this.loading || this.uploaderDocument.isUploading;
    }

    private updateViewAfterSave(): void {
        this.uploaderDocument.clearQueue();
        this.vehicle.procedures.forEach((procedure, index) => {
            if (procedure.id == undefined || procedure.id == null) {
                this.vehicle.procedures.splice(index, 1);
            }
        });
    }

    public checkIfNotValid(): boolean {
        if (!this.form.controls["newProcedureCustomDate"].valid) {
            return true;
        }
        /*!this.form.valid ||*/
        return this.loading || this.uploaderDocument.isUploading || this.uploaderDocument.queue.length == 0;
    }

    private checkPreSaveProcedures(): boolean {
        let hasPreSaveProcedures = false;
        this.vehicle.procedures.forEach(procedure => {
            if (procedure.id === undefined || procedure.id === null) {
                hasPreSaveProcedures = true;
            }
        });
        return hasPreSaveProcedures;
    }

    public getState(procedure: Procedure): string {
        return this.transform(procedure.done, procedure.customDate);
    }

    private transform(done: boolean, date: string): string {

        if (done) {
            return 'RESUELTO';
        }

        /*
        if (unique) {
            return 'A VENCER';
        }
        */

        let todayDate = new Date(Date.parse(Date()));
        let expiratedDateStr: string[];
        let expiratedDate: Date;

        if (date !== undefined && date !== null) {
            if (date.includes('/')) {
                expiratedDateStr = date.split('/');
                expiratedDate = new Date(+expiratedDateStr[2], +expiratedDateStr[1] - 1, +expiratedDateStr[0]);
            } else if (date.includes('-')) {
                expiratedDateStr = date.split('-');
                expiratedDate = new Date(+expiratedDateStr[2], +expiratedDateStr[1] - 1, +expiratedDateStr[0]);
            }
        }

        return expiratedDate > todayDate ? 'A VENCER' : 'VENCIDO';
    }

}


/** WEBPACK FOOTER **
 ** ./src/app/procedures/procedures.editor.component.ts
 **/